import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Core admin users who can edit member profiles and grant admin rights to others
const CORE_ADMIN_EMAILS = [
  "js9640@nyu.edu",      // Josie Simm
  "ks5404@nyu.edu",      // Karen Sheils
  "mxa5271@nyu.edu",     // Mahra Alblooshi
];

// Get all admin emails (core admins + any additional admins set by core admins)
const getAllAdminEmails = () => {
  const savedAdminEmails = localStorage.getItem('staffCouncilAdmins');
  if (savedAdminEmails) {
    try {
      const additionalAdmins = JSON.parse(savedAdminEmails);
      return [...new Set([...CORE_ADMIN_EMAILS, ...additionalAdmins])];
    } catch (error) {
      console.error('Error loading admin emails:', error);
    }
  }
  return CORE_ADMIN_EMAILS;
};

// List of authorized Staff Council member emails
const AUTHORIZED_EMAILS = [
  // Steering Committee
  "js9640@nyu.edu",
  "mwc347@nyu.edu",
  "kjt9854@nyu.edu",
  "akd6@nyu.edu",
  "eo50@nyu.edu",
  "sn1245@nyu.edu",
  "ss5552@nyu.edu",
  "ask9190@nyu.edu",
  // Academic Staff
  "alc9738@nyu.edu",
  "vig211@nyu.edu",
  "iyc1@nyu.edu",
  "sa2696@nyu.edu",
  "wms5@nyu.edu",
  "kjt9854@nyu.edu",
  "ask9190@nyu.edu",
  // Administrative Staff
  "aha9268@nyu.edu",
  "ss5552@nyu.edu",
  "sn1245@nyu.edu",
  "dd2529@nyu.edu",
  "eo50@nyu.edu",
  "jl6159@nyu.edu",
  "slh12@nyu.edu",
  "cn1065@nyu.edu",
  "hem8@nyu.edu",
  "ha50@nyu.edu",
  "js9640@nyu.edu",
  "st5035@nyu.edu",
  "mwc347@nyu.edu",
  "ama10295@nyu.edu",
  // Researchers
  "iam4@nyu.edu",
  "aah698@nyu.edu",
  "mja9908@nyu.edu",
  "ps157@nyu.edu",
  "mb9720@nyu.edu",
  "akd6@nyu.edu",
  "bm3421@nyu.edu",
  // Office of Shared Governance
  "mxa5271@nyu.edu",
  "ks5404@nyu.edu",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('http://localhost:3001/auth/user', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Check if user's email is authorized OR if they are an admin
        if (AUTHORIZED_EMAILS.includes(userData.email) || userData.isAdmin) {
          // Add flag if user is a profile admin (can edit members and manage admin rights)
          const allAdminEmails = getAllAdminEmails();
          userData.isProfileAdmin = allAdminEmails.includes(userData.email);
          setUser(userData);
          setError(null);
        } else {
          setUser(null);
          setError('Access denied. You are not a Staff Council member.');
        }
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = 'http://localhost:3001/auth/google';
  };

  const adminLogin = async (username, password) => {
    try {
      const response = await fetch('http://localhost:3001/auth/admin-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      
      console.log('Admin login response:', data);

      if (response.ok && data.success) {
        console.log('Setting user:', data.user);
        // Admin override users are automatically profile admins
        const userData = { ...data.user, isProfileAdmin: true };
        setUser(userData);
        setError(null);
        return { success: true };
      } else {
        console.log('Login failed:', data.error);
        setError(data.error || 'Invalid credentials');
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error('Admin login failed:', err);
      setError('Login failed. Please try again.');
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = async () => {
    try {
      await fetch('http://localhost:3001/auth/logout', {
        credentials: 'include',
      });
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    adminLogin,
    logout,
    isAuthenticated: !!user,
    isAuthorized: user && (AUTHORIZED_EMAILS.includes(user.email) || user.isAdmin),
    isProfileAdmin: user?.isProfileAdmin || false,
  };

  console.log('AuthContext state:', { user, isAuthenticated: !!user, isAuthorized: user && (AUTHORIZED_EMAILS.includes(user.email) || user.isAdmin) });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

